import { memo, useCallback, useRef, type KeyboardEvent } from 'react';

import { usePrefetch, type PrefetchConfig } from '~/common/hooks/usePrefetch';
import { Pill } from '~/domains/design-system/Pill';
import { Scroller } from '~/domains/design-system/components/Scroller/Scroller';
import testIds from '~/domains/home/testing.const';
import type { PromotedContestTab } from '~/services/marketing/promoted-contests.schema';

import { useStyles } from './CarouselPills.styles';

const ARROW_KEY_STEPS: Record<string, number | undefined> = { ArrowRight: 1, ArrowLeft: -1 };

type CarouselPillProps = {
  tab: PromotedContestTab;
  railId: string;
  isActive: boolean;
  onSelect: (value: string) => void;
  onKeyDown: (event: KeyboardEvent<HTMLButtonElement>) => void;
  prefetchConfig: PrefetchConfig | undefined;
};

const CarouselPill = memo(
  ({ tab, railId, isActive, onSelect, onKeyDown, prefetchConfig }: CarouselPillProps) => {
    const { classes } = useStyles();
    const { onMouseEnter, onMouseLeave, onFocus } = usePrefetch(prefetchConfig);

    const handleClick = useCallback(() => onSelect(tab.value), [onSelect, tab.value]);

    return (
      <Pill
        type="button"
        role="tab"
        className={classes.pill}
        selected={isActive}
        aria-selected={isActive}
        aria-controls={railId}
        tabIndex={isActive ? 0 : -1}
        data-tab-value={tab.value}
        data-test-id={testIds.components.condensedCarousels.pill}
        onClick={handleClick}
        onKeyDown={onKeyDown}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onFocus={onFocus}
      >
        {tab.title}
      </Pill>
    );
  }
);
CarouselPill.displayName = 'CarouselPill';

type CarouselPillsProps = {
  carouselTitle: string;
  railId: string;
  tabs: PromotedContestTab[];
  activeValue: string;
  onSelect: (value: string) => void;
  getPrefetchConfig: (tab: PromotedContestTab) => PrefetchConfig | undefined;
};

export function CarouselPills({
  carouselTitle,
  railId,
  tabs,
  activeValue,
  onSelect,
  getPrefetchConfig,
}: CarouselPillsProps) {
  const { classes, theme } = useStyles();
  const tablistRef = useRef<HTMLDivElement>(null);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLButtonElement>) => {
      const step = ARROW_KEY_STEPS[event.key];
      if (step === undefined) return;

      const currentValue = event.currentTarget.dataset.tabValue;
      const currentIndex = tabs.findIndex((tab) => tab.value === currentValue);
      if (currentIndex === -1) return;

      event.preventDefault();
      const nextIndex = (currentIndex + step + tabs.length) % tabs.length;
      onSelect(tabs[nextIndex].value);
      tablistRef.current?.querySelectorAll<HTMLElement>('[role="tab"]')[nextIndex]?.focus();
    },
    [tabs, onSelect]
  );

  return (
    <Scroller backgroundColor={theme.colors.gray2[0]}>
      <div ref={tablistRef} role="tablist" aria-label={carouselTitle} className={classes.tablist}>
        {tabs.map((tab) => (
          <CarouselPill
            key={tab.value}
            tab={tab}
            railId={railId}
            isActive={tab.value === activeValue}
            onSelect={onSelect}
            onKeyDown={handleKeyDown}
            prefetchConfig={getPrefetchConfig(tab)}
          />
        ))}
      </div>
    </Scroller>
  );
}
